import React, { useState, useEffect, Fragment } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';

import { Alert, Button, ProgressBar } from 'react-bootstrap';
import _ from 'lodash';
import request from 'superagent';
import useDataApi from 'utils/useDataApi';
import {PER_CASE_FLAG} from '../../../components/Integrations/NetDocuments/AttributeSelector';
import usePostApi from 'utils/usePostApi';

const selectStyle = { width: '180px' }; // Fit about 4 in a line on Room Settings Screen

function AttributeConfig({ connection_id, casefolder_id, uid }) {

  const [{ data, isLoading, isError }, { setData }] = useDataApi(
    `/rpc/integrations/connections/${connection_id}/casefolders/${casefolder_id}/netdocuments-profile`,
    {}
  );
  const [newProfile, setNewProfile] = useState(null);

  const [{ data: newProfileSavedResult, isLoading: isSaveInProgress, isError: profileSaveError }, triggerPost]
    = usePostApi(`/rpc/integrations/connections/${connection_id}/casefolders/${casefolder_id}/netdocuments-profile`);

  useEffect(() => {
    if (newProfileSavedResult) setData(newProfileSavedResult);
  }, [newProfileSavedResult]);

  function saveData() {
    triggerPost(newProfile);
    setNewProfile(null);
  }

  if (isLoading) return <ProgressBar active now={100} />;
  if (isError) return <Alert bsStyle="danger">Error Loading Current Config: { ErrorExplanation(isError) } </Alert>;

  return (
    <Fragment>
      <div style={{float: 'left'}}>
        <ProfileSelector
          connection_id={connection_id}
          casefolder_id={casefolder_id}
          uid={uid}
          existingProfileConfig={data}
          onSelectFinal={setNewProfile}
        />
      </div>
      <div style={{float: 'left', marginLeft: '4px', marginTop: '1px'}}>
        <Button disabled={!newProfile} onClick={saveData} bsStyle="primary">
          <i className={`fa ${isSaveInProgress ? 'fa-spinner fa-spinning' : 'fa-save'}`} />
          &nbsp; Save
        </Button>
      </div>
      <div style={{clear: 'both'}}></div>
      <p style={{marginBottom: 0}}>Note: Closed matters are not included and can not be used.</p>
    </Fragment>
  );
}

function ProfileSelector({ connection_id, existingProfileConfig, onSelectFinal }) {
  const [selectedCabinet, setSelectedCabinet] = useState(null);
  const [{ data, isLoading, isError }] = useDataApi(`/rpc/integrations/connections/${connection_id}/cabinets`, {},);
  const cabinets = data.cabinets || [];

  if (isError) return <Alert bsStyle="danger">Error Loading Cabinets</Alert>;

  const cabinetOptions = cabinets.map(cabinet => ({ ...cabinet,
    label: <span><i className="fa fa-archive"></i> { cabinet.name }</span>,
    value: cabinet.id
  }));
  const preselectedOption = _.find(cabinetOptions, { id: existingProfileConfig.cabinet_id });

  // on first load
  useEffect(() => {
    // preselected cabinet
    if (preselectedOption)
      setSelectedCabinet(preselectedOption);
    // auto select if there is only one
    else if (cabinetOptions.length == 1)
      setSelectedCabinet(cabinetOptions[0]);
  }, [existingProfileConfig, data]);

  const onSelectFinalAttribute = (selectedAttributeValues) => {
    onSelectFinal({
      cabinet_id: selectedCabinet.id,
      profile: selectedAttributeValues.map( attrval => ({
        id: attrval.attribute_id,
        value: attrval.value,
      })),
    });
  };

  const selectedCabinetId = _.get(selectedCabinet, 'id', null);
  const preSelectedCabinetId = _.get(preselectedOption, 'id', null);

  return (
    <Fragment>
      <div style={{ display: 'inline-block' }}>
        <Select
          style={selectStyle}
          placeholder="Cabinet"
          isLoading={isLoading}
          options={cabinetOptions}
          value={selectedCabinet}
          onChange={setSelectedCabinet}
        />
      </div>
      {selectedCabinet &&
      <AttributesSelectors
        connection_id={connection_id}
        cabinet={selectedCabinet}
        // NOTE - can't just use _.isEqual(selectedCabinet, preselectedOption) because they contain deep differences in DOM stuff.
        existingProfileConfig={_.isEqual(selectedCabinetId, preSelectedCabinetId) ? existingProfileConfig : {}}
        onSelectFinalAttribute={onSelectFinalAttribute}
      />
      }
    </Fragment>
  );
}

AttributeConfig.propTypes = {
  connection_id: PropTypes.number.isRequired,
  casefolder_id: PropTypes.number.isRequired,
  uid: PropTypes.number.isRequired,
};

function AttributesSelectors({ connection_id, cabinet, onSelectFinalAttribute, existingProfileConfig }) {
  const [{ data: cabinetProfileData, isLoading: cabinetProfileIsLoading, isError: cabinetProfileIsError }] = useDataApi(
    `/rpc/integrations/connections/${connection_id}/cabinets/${cabinet.id}/profile`,
    {},
  );

  const [{ data, isLoading, isError }] = useDataApi(
    `/rpc/integrations/connections/${connection_id}/cabinets/${cabinet.id}/attributes`,
    {},
  );

  if (isError) return <Alert bsStyle="danger">Error Loading List of Attributes for cabinet</Alert>;
  if (cabinetProfileIsError) return <Alert bsStyle="danger">
    Error Loading Cabinet Profile, make sure it is configured
    on <a href="/integrations/show" target="_blank">Connection Configuration Page</a>
  </Alert>;

  // Our Loading bar looks like a disabled select
  if (isLoading || cabinetProfileIsLoading) {
    return (
      <div style={{ display: 'inline-block' }}>
        <Select placeholder="Loading Attributes" style={selectStyle} isLoading disabled />
      </div>
    );
  }

  const attributes = organizingAttributes(cabinet, data.attributes || [], cabinetProfileData);

  if (!attributes.length) return <Alert>Cabinet does not have any organizational attributes</Alert>;

  return <AttributeSelector
    connection_id={connection_id}
    cabinet={cabinet}
    attributes={attributes}
    onSelectFinalAttribute={onSelectFinalAttribute}
    existingProfileConfig={existingProfileConfig}
  />;
}

function AttributeSelector({ connection_id, cabinet, attributes, parent_key = '', onSelectFinalAttribute, existingProfileConfig }) {
  const [attribute, ...childAttributes] = attributes;

  const hasChildren = childAttributes.length > 0;

  const [selected, setSelected] = useState(null);

  // To support non-linked but mandatory attributes (i.e. wsOrgAttr that is specified as "Select per case"
  const parent_key_to_use = attribute.linked ? parent_key : '';

  const [{ data, isLoading, isError }, { fetchData }] = useDataApi(
    `/rpc/integrations/connections/${connection_id}/repository/${cabinet.repositoryId}/attribute/${attribute.id}/values?parent_key=${parent_key_to_use}`,
    {}
  );
  const trigger_refresh = async function () {
    return fetchData(`/rpc/integrations/connections/${connection_id}/repository/${cabinet.repositoryId}/attribute/${attribute.id}/values?parent_key=${parent_key_to_use}&refresh=true`);
  };

  const values = data.values || [];
  const valuesOptions = values.map(val => ({
    ...val,
    // If the key is only digits, include both key and description in the label.
    // IE client code 001 Client 1, or matter: 0001 Matter 1-1
    // This allows easier finding for customers who know the codes and just want to type them in
    label: attributeLabel(val.key, val.description),
    value: val.key,
    attribute_id: attribute.id
  }));
  const existingProfileValue = _.find(existingProfileConfig.profile, { id: attribute.id });
  const preselectedOption = existingProfileValue && _.find(valuesOptions, { value: existingProfileValue.value });

  useEffect(() => {
    // Select existing option when appropriate
    if (preselectedOption)
      setSelected(preselectedOption);
    // Auto Select Attribute value if these is only one option
    else if (valuesOptions.length === 1) {
      setSelected(valuesOptions[0]);
      if (!hasChildren) onSelectFinalAttribute(valuesOptions);
    }
  }, [data, existingProfileConfig]);


  if (isError) return <Alert>Unable to load values for {attribute.name}</Alert>;

  return [
    /* 1. SelectBox for this attribute */
    /* TODO: upgrade react-select to get defaultMenuIsOpen functionality */
    <div style={{ display: 'inline-block' }} key="self">
      <Select
        style={selectStyle}
        autoFocus
        defaultMenuIsOpen
        placeholder={attribute.name}
        isLoading={isLoading}
        options={valuesOptions}
        value={selected}
        onChange={(val) => {
          if (!hasChildren && val) onSelectFinalAttribute([val]);
          return setSelected(val);
        }}
      />
    </div>,
    isLoading ||
    <div
      style={{
        display: 'inline-block',
        position: 'absolute',
        zIndex: 100,
        margin: '9px 0 0 -53px',
        cursor: 'pointer',
      }}
      onClick={trigger_refresh}
      key="refresh-button"
    >
      <i className="fa fa-refresh" />
    </div>,
    /* 2. Any Child Attributes */
    selected && hasChildren &&
    <AttributeSelector
      key={selected.value}
      connection_id={connection_id}
      cabinet={cabinet}
      attributes={childAttributes}
      parent_key={selected.value}
      /* we wrap the onSelect so that we can bubble up all the attribute values to the top  */
      onSelectFinalAttribute={val => onSelectFinalAttribute(val.concat(selected))}
      existingProfileConfig={_.isEqual(selected, preselectedOption) ? existingProfileConfig : {}}
    />,
  ];
}

function attributeLabel(key, description) {
  // no description, return the key
  if (description == null || description.length == 0)
    return key;

  // If the key is not present in the description, include both key and description.
  // Returning both allows autocompletion typing for client/matter codes for example
  if (description.toLowerCase().indexOf(key.toLowerCase()) === -1)
    return key + ' ' + description;
  else
    return description;
}

function organizingAttributes(cabinet, attributes, cabinetProfile) {
  if (!cabinet) return [];
  if (!attributes.length) return [];

  if (!cabinet.wsAttrNum) {
    console.log('Cabinet is not organized in a workspace');
    return [];
  }
  const attributesById = _.keyBy(attributes, attr => attr.id);

  const organizingAttribute = attributesById[cabinet.wsAttrNum];

  if (!organizingAttribute) {
    console.log(`Can not find organizing attribute - no attribute with id '${cabinet.wsAttrNum}' found in`, attributes);
    return [];
  }

  const attributeList = [organizingAttribute];
  let attr = organizingAttribute;
  while (attr.linked) {
    attr = attributesById[attr.linked];
    attributeList.unshift(attr);
  }

  // Sometimes people use a child attribute of the wsAttrNum to organize workspaces
  const workspaceOrgAttr = attributesById[cabinet.wsOrgAttrNum];
  if (workspaceOrgAttr && organizingAttribute.id === workspaceOrgAttr.linked) {
    attributeList.push(workspaceOrgAttr);
  }

  // Attribute that was configured to be per-case
  const perCaseAttributeRef = _.find(cabinetProfile.profile, {value: PER_CASE_FLAG });
  const perCaseAttribute = attributesById[ _.get(perCaseAttributeRef, 'id') ];
  if (perCaseAttribute && !_.find(attributeList, {id: perCaseAttribute.id})) {
    attributeList.push(perCaseAttribute);
  }

  return attributeList;
}

function ErrorExplanation(error) {
  const prettyError = _.get(error, 'response.body.error');
  if (prettyError) return prettyError;

  return `${error}`;
}

export default AttributeConfig;



// WEBPACK FOOTER //
// ./src/CaseFolder/Integrations/NetDocuments/AttributeConfig.jsx